import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { Close } from '@material-ui/icons';
import classNames from 'classnames';
import { array, bool, func, node, object, string } from 'prop-types';

import { useBundleCampaign } from '@lootboy/lib/slices/campaigns';

import Image from '~/components/Image';

import style from './BundleSelector.module.scss';

export const BundleCampaignContext = createContext({});

const Tooltip = ({ campaign, setShow, show, className }) => {
  if (!campaign || !show) {
    return null;
  }

  return (
    <div className={classNames(style.campaignTooltip, className)}>
      <div>
        <p>{campaign.header}</p>
        <p>{campaign.text}</p>
      </div>
      <Image
        alt=""
        path={campaign.image}
      />
      <Close
        className={style.campaignClose}
        onClick={() => setShow(false)}
      />
    </div>
  );
};

Tooltip.propTypes = {
  campaign: object,
  setShow: func,
  show: bool,
  className: string,
};

const BundleCampaignProvider = ({
  _id,
  currency,
  bundleSelected,
  className,
  lootBundles,
  children,
}) => {
  const campaigns = useBundleCampaign({
    _id,
    currency,
    lootBundles,
  });

  const [show, setShow] = useState(true);
  const bundleToIndicateRef = useRef('');

  const state = useMemo(() => {
    const bundles = Object.keys(campaigns);

    if (
      !bundleToIndicateRef.current ||
      !bundles.includes(bundleToIndicateRef.current)
    ) {
      [bundleToIndicateRef.current] = bundles;
    }
    bundleToIndicateRef.current = campaigns[bundleSelected]
      ? bundleSelected
      : bundleToIndicateRef.current;

    return {
      show,
      bundles,
      bundleCampaignToIndicate: campaigns[bundleToIndicateRef.current],
      setShow,
      tooltip: (
        <Tooltip
          campaign={campaigns[bundleToIndicateRef.current]}
          setShow={setShow}
          show={show}
          className={className}
        />
      ),
    };
  }, [bundleSelected, campaigns, className, show]);

  useEffect(() => {
    if (bundleSelected) {
      setShow(true);
    }
  }, [bundleSelected]);

  return (
    <BundleCampaignContext.Provider value={state}>
      {children}
    </BundleCampaignContext.Provider>
  );
};

BundleCampaignProvider.propTypes = {
  _id: string,
  currency: string,
  bundleSelected: string,
  children: node,
  className: string,
  lootBundles: array,
};

export default BundleCampaignProvider;
